import { PageHeader } from "@ant-design/pro-components";
import { Card } from "antd";
import { useA1ResolvedNav } from "@/hooks/useA1ResolvedNav";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ColumnMappingStep,
  FileUploadStep,
  SchemaSelectionStep,
} from "./components";
import { useImportState } from "./hooks/useImportState";
import "./importAddPage.css";

const ImportAddPage = () => {
  const navigationRoutes = useA1ResolvedNav();
  const navigate = useNavigate();
  const [isSchemaLoading, setIsSchemaLoading] = useState(false);

  const {
    // State
    selectedSchema,
    setSelectedSchema,
    selectedSchemaId,
    setSelectedSchemaId,
    importId,
    setImportId,
    fileColumns,
    setFileColumns,
    currentStep,
    setCurrentStep,
    isUploading,
    setIsUploading,
    isUpdatingMapping,
    isRunningImport,
    isMappingSaved,
    setIsMappingSaved,
    importType,
    setImportType,
    // Actions
    handleRunImportClick,
  } = useImportState();

  // Update selected schema when schema ID changes
  useEffect(() => {
    if (selectedSchemaId) {
      // This would need to be implemented based on your schema options
      // For now, we'll use the ID as the schema name
      setSelectedSchema(selectedSchemaId);
    }
  }, [selectedSchemaId, setSelectedSchema]);

  const handleSchemaContinue = async (schemaLabel?: string) => {
    try {
      setIsSchemaLoading(true);
      if (schemaLabel) {
        setSelectedSchema(schemaLabel);
      }
      setCurrentStep(2);
    } catch (error) {
      console.error("Error in schema selection:", error);
    } finally {
      setIsSchemaLoading(false);
    }
  };

  const handleFileUpload = (importId: string) => {
    setImportId(importId);
  };

  const handleStepComplete = () => {
    setCurrentStep(3);
  };

  const handleMappingUpdate = (saved: boolean) => {
    setIsMappingSaved(saved);
  };

  const handleResetMapping = () => {
    setIsMappingSaved(false);
  };

  // Check if any action is in progress
  const isAnyActionInProgress =
    isUploading || isUpdatingMapping || isRunningImport || isSchemaLoading;

  return (
    <div className="import-add-page min-w-0 p-4 md:p-0">
      <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={`Add Import`}
          onBack={() => navigate(navigationRoutes.IMPORTS)}
        />
      }
    >
      <div className="space-y-6 sm:space-y-8">
        {/* Step 1: Schema Selection */}
        {currentStep >= 1 && (
          <SchemaSelectionStep
            selectedSchemaId={selectedSchemaId}
            setSelectedSchemaId={setSelectedSchemaId}
            importType={importType}
            setImportType={setImportType}
            currentStep={currentStep}
            onContinue={handleSchemaContinue}
            isLoading={isSchemaLoading}
            disabled={isAnyActionInProgress}
          />
        )}

        {/* Step 2: File Upload */}
        {currentStep >= 2 && (
          <FileUploadStep
            currentStep={currentStep}
            isUploading={isUploading}
            selectedSchemaId={selectedSchemaId}
            importType={importType}
            onFileUpload={handleFileUpload}
            onStepComplete={handleStepComplete}
            setFileColumns={setFileColumns}
            setIsUploading={setIsUploading}
            disabled={isAnyActionInProgress}
          />
        )}

        {/* Step 3: Column Mapping */}
        {currentStep >= 3 && (
          <ColumnMappingStep
            selectedSchema={selectedSchema}
            fileColumns={fileColumns}
            importId={importId}
            isUpdatingMapping={isUpdatingMapping}
            isRunningImport={isRunningImport}
            isMappingSaved={isMappingSaved}
            onMappingUpdate={handleMappingUpdate}
            onImportRun={handleRunImportClick}
            onResetMapping={handleResetMapping}
            disabled={isAnyActionInProgress}
          />
        )}
      </div>
    </Card>
    </div>
  );
};

export default ImportAddPage;
